import React, { useState } from 'react'
import { AlertTriangle, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useMemoryFacts } from '../hooks/useMemoryFacts'
import type { MemoryFact, MemoryFactCategory } from '../types'

type Props = {
  workspaceId: string
  role: 'owner' | 'admin' | 'member'
}

const CATEGORY_LABELS: Record<MemoryFactCategory, string> = {
  preference: 'Preference',
  account: 'Account',
  process: 'Process',
  goal: 'Goal',
  other: 'Other',
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

const FactRow: React.FC<{
  fact: MemoryFact
  showScopeBadge: boolean
  deleting: boolean
  error?: string
  onDelete: () => void
}> = ({ fact, showScopeBadge, deleting, error, onDelete }) => {
  const { currentTheme: t } = useTheme()
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-2"
      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span
              className="text-[11px] font-medium px-1.5 py-0.5 rounded-md"
              style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
            >
              {CATEGORY_LABELS[fact.category]}
            </span>
            {showScopeBadge && (
              <span
                className="text-[11px] font-medium px-1.5 py-0.5 rounded-md"
                style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
              >
                {fact.scope === 'workspace' ? 'Workspace' : 'Personal'}
              </span>
            )}
          </div>
          <p className="text-sm break-words" style={{ color: t.text }}>{fact.content}</p>
          <p className="text-[12px] mt-1" style={{ color: t.mutedText }}>
            Learned {formatDate(fact.createdAt)} &middot; from conversation {fact.sourceConversationId.slice(0, 8)}
          </p>
        </div>
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          aria-label="Forget this fact"
          className="shrink-0 flex items-center justify-center h-8 w-8 rounded-lg transition-colors disabled:opacity-50"
          style={{ color: t.mutedText }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.errorBackground; e.currentTarget.style.color = t.errorText }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = t.mutedText }}
        >
          {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
        </button>
      </div>
      {error && <span className="text-[12px]" style={{ color: t.errorText }}>{error}</span>}
    </div>
  )
}

const FactsListSection: React.FC<Props> = ({ workspaceId, role }) => {
  const { currentTheme: t } = useTheme()
  const {
    facts, loading, loadingMore, hasMore, error, retry, loadMore,
    deletingIds, deleteErrors, deleteFact,
    purging, purgeError, purgeAll,
  } = useMemoryFacts(workspaceId)
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false)
  const [purgeResult, setPurgeResult] = useState<number | null>(null)

  const isOwnerOrAdmin = role === 'owner' || role === 'admin'

  const handlePurge = async () => {
    const result = await purgeAll()
    if (result) {
      setPurgeResult(result.factCount)
      setPurgeDialogOpen(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: t.text }}>What Zeph remembers</h3>
          <p className="text-[12.5px] mt-0.5" style={{ color: t.mutedText }}>
            {isOwnerOrAdmin
              ? 'Workspace facts and every member’s personal facts. Deleting a fact stops Zeph from using it immediately.'
              : 'Facts Zeph has learned about you personally. Deleting a fact stops Zeph from using it immediately.'}
          </p>
        </div>
        {role === 'owner' && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPurgeDialogOpen(true)}
            className="shrink-0 h-8 px-3 rounded-lg text-xs font-medium"
            style={{ backgroundColor: t.errorBackground, color: t.errorText, border: 'none' }}
          >
            Purge all memory
          </Button>
        )}
      </div>

      {purgeResult !== null && (
        <div className="text-[12.5px] rounded-lg px-3 py-2" style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}>
          Purged {purgeResult} {purgeResult === 1 ? 'fact' : 'facts'} and all conversation recall and rolling profiles for this workspace.
        </div>
      )}

      {loading && (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading remembered facts">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.errorBackground }}>
            <AlertTriangle size={24} style={{ color: t.errorText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load remembered facts</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error}</p>
          </div>
          <button
            type="button"
            onClick={retry}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && facts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
          <p className="font-semibold text-sm" style={{ color: t.text }}>Nothing remembered yet</p>
          <p className="text-[12.5px]" style={{ color: t.mutedText }}>
            {isOwnerOrAdmin
              ? 'Facts Zeph learns from conversations will show up here.'
              : 'Facts Zeph learns about you will show up here.'}
          </p>
        </div>
      )}

      {!loading && !error && facts.length > 0 && (
        <div className="flex flex-col gap-2">
          {facts.map((fact) => (
            <FactRow
              key={fact.id}
              fact={fact}
              showScopeBadge={isOwnerOrAdmin}
              deleting={!!deletingIds[fact.id]}
              {...(deleteErrors[fact.id] !== undefined ? { error: deleteErrors[fact.id] } : {})}
              onDelete={() => void deleteFact(fact.id)}
            />
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full text-center text-xs font-medium py-2.5 rounded-xl border transition disabled:opacity-50"
              style={{ color: t.text, borderColor: t.border, backgroundColor: t.sectionBackground }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}

      <Dialog open={purgeDialogOpen} onOpenChange={(open) => { if (!open && !purging) setPurgeDialogOpen(false) }}>
        <DialogContent
          className="py-6 px-8 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: t.text }}>
              Purge all workspace memory?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-center mt-1" style={{ color: t.mutedText }}>
            This permanently deletes every remembered fact, rolling profile, and conversation recall for this
            workspace &mdash; for everyone. This cannot be undone.
          </p>
          {purgeError && <p className="text-sm text-center mt-2" style={{ color: t.errorText }}>{purgeError}</p>}
          <DialogFooter className="flex justify-center gap-3 mt-6 flex-row sm:justify-center">
            <Button
              variant="secondary"
              onClick={() => setPurgeDialogOpen(false)}
              disabled={purging}
              className="px-5 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handlePurge()}
              disabled={purging}
              className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium h-auto text-sm"
            >
              {purging ? 'Purging…' : 'Purge everything'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(FactsListSection)
